import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { formatDateLong } from '../../../services/helperService';
import { useTranslation } from 'react-i18next';
import { useAppUserDefaultStore, isAllRestaurantsSelected, saveVatSetting } from '../../../stores/appUserDefaultStore';
import { RevenueItem } from '../../../services/revenuehistory/revenueHistoryService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { eur, getTotalRevenue } from '../../revenuehistory/utils';
import { GmailIdCopy } from '../orderHistory/GmailCopy';
import { DropdownSelector } from '../../ui/DropdownSelector';
import { FilterOption } from '../../ui/TabbedSections';
import AppFooter from '../../common/AppFooter';
import { primaryText, semiBoldText, xxLargeTextSize } from '../../../styling/stylingHelper';
import { cn } from '../../../lib/utils';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';

const vatOptions: FilterOption[] = [
  { value: 'excl', label: 'Excl. VAT' },
  { value: 'incl', label: 'Incl. VAT' },
];

// Component to display individual revenue item
const RevenueItemDisplay = ({ item, showExVat }: { item: RevenueItem; showExVat: boolean }) => {
  const itemPrice = showExVat ? (item.exVat || 0) : (item.price || 0);
  const unitPrice = itemPrice / (item.quantity || 1);

  return (
    <div className="flex items-center justify-between p-3 ml-6 bg-background dark:bg-background-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 rounded-xl border border-border dark:border-border-dark transition-colors">
      <div className="flex-1">
        <div className="font-semibold text-text dark:text-text-dark text-base">
          {item.name || 'Unknown Item'}
        </div>
        <div className="text-sm text-muted dark:text-muted-dark font-mono mt-1 flex items-center gap-3">
          <span>Quantity: {item.quantity}</span>
          <span>Unit Price: {eur(unitPrice)}</span>
        </div>
      </div>
      <div className="text-right ml-4">
        <div className="text-lg font-bold text-text dark:text-text-dark">
          {eur(itemPrice)}
        </div>
      </div>
    </div>
  );
};

const RevenueHistoryDetailPage = () => {
  const { t } = useTranslation();
  const { revenueId } = useParams<{ revenueId: string }>();
  const navigate = useNavigate();
  const load = useRevenueHistory(s => s.load);
  const revenue = useRevenueHistory(s => s.byId(revenueId ?? ''));
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const showExVat = useAppUserDefaultStore(s => s.showExVat);

  // Check if "All Restaurants" is selected
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);

  // Revenue source store for loading data
  const loadRevenueSourceData = useRestaurantStore(s => s.loadAllData);
  const revenueSourcesMap = useRestaurantStore(s => s.restaurantRevenueSources);

  useEffect(() => {
    loadRevenueSourceData();
  }, [loadRevenueSourceData]);

  useEffect(() => {
    if (!revenueId || !restaurantId) return;
    load(restaurantId, revenueSourcesMap);
  }, [load, restaurantId, revenueId, revenueSourcesMap]);

  useEffect(() => {
    const scroller = document.getElementById('mainContainer') as HTMLDivElement | null;
    if (!scroller) return;
    scroller.scrollTop = 0;
  }, []);

  if (!revenue) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted dark:text-muted-dark">Loading…</p>
      </div>
    );
  }

  const formattedDate = formatDateLong(new Date(revenue.date));

  // Sort categories by revenue
  const sortedCategories = revenue.revenueData.categories
    .map(category => {
      const categoryRevenue = category.items.reduce(
        (acc, item) => acc + (showExVat ? (item.exVat || 0) : (item.price || 0)),
        0
      );
      return { ...category, categoryRevenue };
    })
    .sort((a, b) => b.categoryRevenue - a.categoryRevenue);

  const totalRevenue = getTotalRevenue(revenue, showExVat);

  // Calculate category totals for the breakdown section
  const categoryTotals = sortedCategories.map(category => ({
    name: category.name,
    revenue: category.categoryRevenue
  }));

  // Count total items across all categories
  const totalItemsCount = revenue.revenueData.categories.reduce(
    (count, category) => count + category.items.length,
    0
  );

  // Handler for VAT toggle
  const handleVatChange = async (value: string) => {
    const showExVatValue = value === 'excl';
    try {
      await saveVatSetting(showExVatValue);
    } catch (error) {
      console.error('Failed to save VAT setting:', error);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div>
      <MobilePageHeader title={t('revenueHistory.revenueDetails')} />
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        {/* Left side: back and title */}
        <div className="flex items-center">
          <ArrowLeft className="w-6 h-6 mt-1 mr-2 cursor-pointer" onClick={handleGoBack} />
          <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText, 'hidden lg:block')}>
            {t('revenueHistory.revenueDetails')}
          </h1>
        </div>
        {/* Right side: VAT toggle */}
        <div className="flex items-center gap-2">
          <DropdownSelector
            options={vatOptions}
            defaultValue={showExVat ? 'excl' : 'incl'}
            onChange={handleVatChange}
          />
        </div>
      </div>

      {/* Content sections with consistent spacing */}
      <div className="mt-4 mb-4 flex flex-col gap-6">
        {/* Revenue Header Info */}
        <div className="w-full">
          <div className="bg-card dark:bg-card-dark rounded-xl p-6">
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-2xl font-bold text-text dark:text-text-dark">{formattedDate}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
              <div className="flex gap-3">
                <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                  {t('revenueHistory.source')}:
                </span>
                <span className="text-text dark:text-text-dark capitalize font-semibold">
                  {revenue.revenueSource}
                </span>
              </div>
              <div className="hidden sm:flex gap-3 items-center">
                <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                  Email:
                </span>
                <GmailIdCopy gmailId={revenue.gmailId} />
              </div>
              {isAllRestaurants && revenue.restaurantName && (
                <div className="flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('revenueHistory.restaurant')}:
                  </span>
                  <span className="text-text dark:text-text-dark font-semibold">
                    {revenue.restaurantName}
                  </span>
                </div>
              )}
              {revenue.revenueSourceType && (
                <div className="flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('revenueHistory.section')}:
                  </span>
                  <span className="text-text dark:text-text-dark font-semibold">
                    {revenue.revenueSourceType}
                  </span>
                </div>
              )}
              {revenue.sourceFileUrl && (
                <div className="hidden sm:flex gap-3">
                  <span className="font-medium text-muted dark:text-muted-dark min-w-[120px]">
                    {t('orderHistory.sourceFile')}:
                  </span>
                  <a
                    href={revenue.sourceFileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {t('orderHistory.viewFile')}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="w-full">
          <div className="bg-card dark:bg-card-dark rounded-xl p-4">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-text dark:text-text-dark">
                {t('revenueHistory.categoryBreakdown')}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryTotals.map((category) => (
                <div key={category.name} className="bg-background dark:bg-background-dark p-4 rounded-xl border border-border dark:border-border-dark">
                  <div className="text-sm text-muted dark:text-muted-dark">{category.name}</div>
                  <div className="text-lg font-semibold text-text dark:text-text-dark">{eur(category.revenue)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="w-full">
          <div className="bg-card dark:bg-card-dark rounded-xl p-4">
            <div className="mb-4">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-text dark:text-text-dark">
                  {t('revenueHistory.revenueItems', { count: totalItemsCount })}
                </h2>
                <div className="text-right">
                  <p className="text-lg font-semibold text-text dark:text-text-dark">
                    {t('revenueHistory.totalRevenue')}: {eur(totalRevenue)}
                  </p>
                </div>
              </div>
            </div>

            {/* Categories and Items */}
            <div>
              {sortedCategories.length > 0 ? (
                <div className="space-y-4">
                  {sortedCategories.map((category, categoryIndex) => (
                    <div key={category.name} className="space-y-2">
                      {/* Category Header */}
                      <div className="flex items-center justify-between p-4 bg-muted/10 dark:bg-muted-dark/10 rounded-xl border-l-4 border-l-green-500">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-text dark:text-text-dark">
                            {category.name}
                          </h3>
                          <span className="text-sm text-muted dark:text-muted-dark">
                            ({category.items.length} {category.items.length === 1 ? 'item' : 'items'})
                          </span>
                        </div>
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {eur(category.categoryRevenue)}
                        </div>
                      </div>

                      {/* Category Items */}
                      <div className="space-y-2">
                        {category.items
                          .sort((a, b) => {
                            const aPrice = showExVat ? (a.exVat || 0) : (a.price || 0);
                            const bPrice = showExVat ? (b.exVat || 0) : (b.price || 0);
                            return bPrice - aPrice;
                          })
                          .map((item, itemIndex) => (
                          <RevenueItemDisplay
                            key={`${category.name}-${item.name}-${itemIndex}`}
                            item={item}
                            showExVat={showExVat}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-muted dark:text-muted-dark">
                  {t('revenueHistory.noItemsFound')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AppFooter />
    </div>
  );
};

export default RevenueHistoryDetailPage;